import React, {useEffect, useRef} from "react";
import clsx from "clsx";

import {DeviceStatus, ExplorerNode, ExplorerNodeType, StatusCounts} from "./buildTree";
import {ChevronIcon, DeviceIcon, GroupIcon, SensorIcon, SiteIcon} from "./icons";
import StatusChip from "./StatusChip";
import StatusDot from "./StatusDot";

import classes from "./DevicesExplorer.module.scss";

const NODE_ICONS: Record<ExplorerNodeType, React.FC<{ size?: number }>> = {
    site: SiteIcon,
    device: DeviceIcon,
    group: GroupIcon,
    sensor: SensorIcon,
};

const STATUS_ROW_CLASS: Record<DeviceStatus, string | undefined> = {
    healthy: undefined,
    offline: classes.rowOffline,
    warning: classes.rowWarning,
    alarm: classes.rowAlarm,
};

interface RowProps {
    node: ExplorerNode;
    depth: number;
    expandable: boolean;
    expanded: boolean;
    selected: boolean;
    loading: boolean;
    status?: DeviceStatus;
    siteCounts?: StatusCounts;
    onToggle: () => void;
    onSelect: () => void;
}

const Row: React.FC<RowProps> = ({
                                      node,
                                      depth,
                                      expandable,
                                      expanded,
                                      selected,
                                      loading,
                                      status,
                                      siteCounts,
                                      onToggle,
                                      onSelect,
                                  }) => {
    const Icon = NODE_ICONS[node.nodeType];

    return (
        <div
            className={clsx(classes.row, status && STATUS_ROW_CLASS[status], selected && classes.rowSelected)}
            style={{paddingLeft: 8 + depth * 16}}
            onClick={onSelect}
            data-node-key={node.key}
        >
            <span
                className={clsx(classes.chevron, expanded && classes.chevronOpen)}
                onClick={(event) => {
                    if (expandable) {
                        event.stopPropagation();
                        onToggle();
                    }
                }}
            >
                {expandable && <ChevronIcon size={13}/>}
            </span>
            <span className={classes.icon}>
                <Icon size={node.nodeType === "sensor" ? 8 : 14}/>
            </span>
            {status && <StatusDot status={status}/>}
            <span className={classes.label}>{node.label}</span>
            {loading && <span className={classes.badge}>…</span>}
            {!loading && typeof node.childCount === "number" && node.childCount > 0 && (
                <span className={classes.badge}>{node.childCount}</span>
            )}
            {siteCounts && (
                <>
                    <StatusChip status="healthy" count={siteCounts.healthy}/>
                    <StatusChip status="warning" count={siteCounts.warning}/>
                    <StatusChip status="alarm" count={siteCounts.alarm}/>
                    <StatusChip status="offline" count={siteCounts.offline}/>
                </>
            )}
        </div>
    );
};

interface ExplorerTreeProps {
    nodes: ExplorerNode[];
    expanded: Set<string>;
    forceExpand: boolean;
    loadingKeys: Set<string>;
    selectedKey?: string;
    deviceStatus: Record<number, DeviceStatus>;
    problemNodeStatus: Record<string, DeviceStatus>;
    siteCounts: Record<number, StatusCounts>;
    onToggle: (node: ExplorerNode) => void;
    onSelect: (node: ExplorerNode) => void;
}

const ExplorerTree: React.FC<ExplorerTreeProps> = ({
                                                        nodes,
                                                        expanded,
                                                        forceExpand,
                                                        loadingKeys,
                                                        selectedKey,
                                                        deviceStatus,
                                                        problemNodeStatus,
                                                        siteCounts,
                                                        onToggle,
                                                        onSelect,
                                                    }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!selectedKey || !containerRef.current) {
            return;
        }
        const selectedRow = containerRef.current.querySelector(`[data-node-key="${selectedKey}"]`);
        selectedRow?.scrollIntoView({block: "nearest"});
    }, [selectedKey]);

    const renderNode = (node: ExplorerNode, depth: number): React.ReactNode => {
        const expandable = node.nodeType !== "sensor";
        const isExpanded = expandable && (forceExpand || expanded.has(node.key));
        const status = node.nodeType === "device" && node.deviceId !== undefined
            ? deviceStatus[node.deviceId]
            : (node.nodeType === "group" || node.nodeType === "sensor")
                ? problemNodeStatus[node.key]
                : undefined;

        return (
            <React.Fragment key={node.key}>
                <Row
                    node={node}
                    depth={depth}
                    expandable={expandable}
                    expanded={isExpanded}
                    selected={node.key === selectedKey}
                    loading={loadingKeys.has(node.key)}
                    status={status}
                    siteCounts={node.nodeType === "site" ? siteCounts[node.locationId] : undefined}
                    onToggle={() => onToggle(node)}
                    onSelect={() => onSelect(node)}
                />
                {isExpanded && (node.children || []).map((child) => renderNode(child, depth + 1))}
            </React.Fragment>
        );
    };

    if (nodes.length === 0) {
        return <div className={classes.empty}>No devices match this filter.</div>;
    }

    return <div className={classes.treeBody} ref={containerRef}>{nodes.map((node) => renderNode(node, 0))}</div>;
};

export default ExplorerTree;
